import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import api, { shieldFetch } from '../services/api';

import AddVehicleForm from './AddVehicleForm';
import VehicleDetails from './VehicleDetails';
import LockedVehiclePanel from './LockedVehiclePanel';
import { usePrivacy } from '../privacy/PrivacyContext';
import { canCreateVehicle as canCreateVehicleForUser } from '../account/access';

const apiUrl = process.env.REACT_APP_URL_LOCAL || "https://192.168.0.17:1234";

const TabsDashBoard = () => {
  const [data, setData] = useState([]);
  const [user, setUser] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isPrivacyMode } = usePrivacy();
  const selectedVehicleId = searchParams.get('vehicleId');
  const isAddVehicleView = searchParams.get('addVehicle') === 'true';
  const canCreateVehicle = canCreateVehicleForUser(user);
  const selectedVehicle = useMemo(() => {
    if (data.length === 0) return null;
    if (!selectedVehicleId) return data[0];
    return data.find((item) => String(item.VehiculeID) === String(selectedVehicleId)) || null;
  }, [data, selectedVehicleId]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await api.get('users/me');
        setUser(response.data);
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
        navigate('/login');
      }
    };

    fetchUserProfile();
  }, [navigate]);

  const fetchVehicles = useCallback(async () => {
    if (user) {
      try {
        const [activeResponse, soldResponse] = await Promise.all([
          shieldFetch(`${apiUrl}/api/vehicule/${user.UtilisateurID}`),
          shieldFetch(`${apiUrl}/api/vehicule/${user.UtilisateurID}/sold`),
        ]);
        if (!activeResponse.ok || !soldResponse.ok) {
          throw new Error('Network response was not ok');
        }
        const [activeResult, soldResult] = await Promise.all([
          activeResponse.json(),
          soldResponse.json(),
        ]);
        setData([
          ...(Array.isArray(activeResult) ? activeResult : []),
          ...(Array.isArray(soldResult) ? soldResult : []),
        ]);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    }
  }, [user]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  useEffect(() => {
    if (isPrivacyMode && isAddVehicleView) {
      navigate('/vehicule', { replace: true });
    }
  }, [isAddVehicleView, isPrivacyMode, navigate]);

  useEffect(() => {
    if (user && selectedVehicle && !selectedVehicle.EstBloqueParQuota && !isAddVehicleView) {
      let ignore = false;
      const fetchVehicleDetails = async () => {
        setVehicleDetails(null);
        try {
          const response = await shieldFetch(`${apiUrl}/api/vehicule/${user.UtilisateurID}/${selectedVehicle.VehiculeID}`);
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          const result = await response.json();
          if (!ignore) {
            setVehicleDetails(result);
          }
        } catch (error) {
          if (!ignore) {
            console.error('Failed to fetch vehicle details:', error);
          }
        }
      };

      fetchVehicleDetails();
      return () => {
        ignore = true;
      };
    }
  }, [user, selectedVehicle, isAddVehicleView]);

  const handleAddVehicle = async (newVehicle) => {
    try {
      const response = await shieldFetch(`${apiUrl}/api/vehicule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newVehicle),
      });

      if (!response.ok) {
        throw new Error('Failed to add vehicle');
      }

      const updatedData = await response.json();
      setData([...data, updatedData]);
      fetchVehicles(); // Recharge la liste des véhicules
    } catch (error) {
      console.error('Failed to add vehicle:', error);
    }
  };

  return (
    <div className='mb-8'>
      <div className="text-neutral-100">
        {!isAddVehicleView ? (
          selectedVehicle?.EstBloqueParQuota
            ? <LockedVehiclePanel vehicle={selectedVehicle} user={user} />
            : <VehicleDetails vehicle={vehicleDetails} onRefresh={fetchVehicles} user={user} />
        ) : canCreateVehicle ? (
          <AddVehicleForm onAddVehicle={handleAddVehicle} onRefresh={fetchVehicles} />
        ) : (
          <section className="rounded-xl border border-amber-500/30 bg-slate-950/70 p-8 text-center text-slate-200">
            <h2 className="text-xl font-black text-white">Ajout de véhicule indisponible</h2>
            <p className="mt-2 text-sm text-slate-400">Vérifiez les droits, le quota ou l’abonnement de votre compte.</p>
            <Link to="/abonnement" className="mt-5 inline-flex rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-sky-500">Voir l’abonnement et les limites</Link>
          </section>
        )}
      </div>
    </div>
  );
};

export default TabsDashBoard;
